import type { SendReminder } from '.'

// The out-of-the-box reminder schedule for the node's defaultConfig, used when
// a reminder node is added on its own. A new reminder chases an invoice a week
// after it falls overdue — enough time for a customer to pay before nudging,
// while still following up promptly.
export const DEFAULT_SEND_REMINDER: SendReminder = {
  type: 'AFTER_DUE_DATE',
  duration: { days: 7 }
}

export const REMINDER_NODE_TEMPLATE_VARIABLES = {
  contactEmail: 'Contact email',
  contactName: 'Contact name',
  customerName: 'Customer name',
  invoiceNumber: 'Invoice number',
  amount: 'Amount',
  dueDate: 'Due date',
  daysLate: 'Days late',
  url: 'Invoice URL'
} as const satisfies Record<string, string>

// The backend key each variable resolves against (e.g. `contactName`), as
// opposed to its human-readable label. `as const satisfies` above preserves
// these literal keys so builders can be constrained to valid variables.
export type ReminderTemplateVariableKey =
  keyof typeof REMINDER_NODE_TEMPLATE_VARIABLES

export const REMINDER_NODE_RECIPIENT_TEMPLATE_VARIABLES: Record<
  string,
  string
> = {
  contactEmail: REMINDER_NODE_TEMPLATE_VARIABLES.contactEmail
}

export const REMINDER_NODE_LINK_TEMPLATE_VARIABLES: Record<string, string> = {
  url: REMINDER_NODE_TEMPLATE_VARIABLES.url
}

/**
 * The single source of truth for the conductor task reference names the
 * reminder node creates. Reference these instead of repeating the strings —
 * the execution summary, manual task content, mocks and tests all key off
 * them.
 */
export const REMINDER_TASK_NAMES = {
  getMainWaitUntilValue: 'get_main_wait_until_value',
  mainFork: 'main_fork',
  mainWait: 'main_wait',
  getDataFork: 'get_data_fork',
  getDataInvoice: 'get_data_invoice',
  getDataCustomer: 'get_data_customer',
  getDataJoin: 'get_data_join',
  mainValidForDunningCheck: 'main_valid_for_dunning_check',
  mainValidForDunningCheckTerminate: 'main_valid_for_dunning_check_terminate',
  // The payment-terms guard on a before-due reminder: skips the send when the invoice's own
  // payment terms are no shorter than the reminder offset (a net-7 invoice's "7 days before due"
  // is the day it was sent). Names must match the backend's DunningWorkflowPaymentTermsGuard,
  // which uses them to recognise an already-guarded workflow during the BADGE-6337 backfill.
  mainValidForPaymentTermsCheck: 'main_valid_for_payment_terms_check',
  paymentTermsCheckComplete: 'payment_terms_check_complete',
  generateInvoiceReminder: 'generate_invoice_reminder',
  manualReview: 'manual_review',
  sendInvoiceReminder: 'send_invoice_reminder',
  sendInvoiceReminderWithoutFailureReview:
    'send_invoice_reminder_without_failure_review',
  replacementFailureSwitch: 'replacement_failure_switch',
  replacementFailureManualReview: 'replacement_failure_manual_review',
  replacementFailureSendInvoiceReminderAfterReview:
    'replacement_failure_send_invoice_reminder_after_review',
  replacementFailureComplete: 'replacement_failure_complete',
  mainJoin: 'main_join',
  // The resolution watcher: a single event-driven WAIT (no timer), parallel to the reminder branch.
  // The platform completes it on a paid/void/uncollectible event, so it stays parked for the whole
  // node — including any manual-review window — rather than expiring at the send time. On completion
  // it re-checks the invoice and, if no longer dunnable, cancels the pending review and terminates
  // the workflow. Replaces the old polling do-while.
  invoiceResolutionWait: 'invoice_resolution_wait',
  invoiceResolutionGetInvoice: 'invoice_resolution_get_invoice',
  invoiceResolutionCheck: 'invoice_resolution_check',
  invoiceResolutionCancelManualTask: 'invoice_resolution_cancel_manual_task',
  invoiceResolutionTerminate: 'invoice_resolution_terminate',
  // A point-in-time dunning re-check run after a human review completes and immediately before
  // the send. The review window is unbounded and the resolution watcher has already timed out at
  // `until` before review starts, so this re-fetches the invoice and terminates the workflow if it
  // is no longer dunnable — preventing a reminder for an invoice paid/voided/uncollectible during
  // review. The approval and replacement-failure review paths never coexist in one workflow, so
  // they reuse these reference names.
  postReviewGetInvoice: 'post_review_get_invoice',
  postReviewValidForDunningCheck: 'post_review_valid_for_dunning_check',
  postReviewValidForDunningCheckTerminate:
    'post_review_valid_for_dunning_check_terminate'
} as const satisfies Record<string, string>

export type ReminderTaskName =
  (typeof REMINDER_TASK_NAMES)[keyof typeof REMINDER_TASK_NAMES]
